import { Component, type PropsWithChildren, type ReactNode } from "react";
import { Button } from "@/components/ui/button";

type State = { error: Error | null };

/**
 * Catches a render error in a page so it does not take the whole admin app
 * down with it.
 *
 * Without this, a throw during render unmounts the entire tree and leaves a
 * blank, unresponsive screen with nothing in the UI to say why. That is how a
 * hooks-order bug in UserDetail presented: "the page freezes on click", with no
 * indication that a specific component had thrown. The bug had been there since
 * the impersonation panel was added and only surfaced once something linked to
 * that page.
 *
 * Deliberately shows the message and stack. This is an internal tool used by
 * the people who can fix the bug, so hiding the cause behind a friendly
 * apology costs a debugging session and buys nothing.
 */
export class RouteErrorBoundary extends Component<PropsWithChildren, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error("Admin route crashed:", error, info?.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="container mx-auto px-4 py-4 md:px-6 md:py-6">
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4">
          <h1 className="text-lg font-semibold text-red-300">This page crashed</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            The rest of the admin app is still usable. Details below, and in the
            browser console.
          </p>
          <pre className="mt-3 overflow-auto rounded-md bg-background p-3 text-xs text-red-200">
            {error.message}
          </pre>
          {error.stack && (
            <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-background p-3 text-[11px] text-muted-foreground">
              {error.stack}
            </pre>
          )}
          <Button
            variant="secondary"
            className="mt-3"
            onClick={() => this.setState({ error: null })}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }
}
